import { Box, Collapse, RadioGroup, Typography } from "@mui/material";
import { DotLegalHeader, DotLegalRadioButton, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalFileUploadWrapper from "@common/components/dotLegalFileUpload/DotLegalFileUploadWrapper";
import { useDocumentPreviewManager } from "@common/components/documentPreview/useDocumentPreviewManager";
import { EntityType } from "@common/Common.types";
import { DocumentPreviewable } from "@common/components/documentPreview/DocumentPreview.types";
import PaneLayout from "../../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../../main/panes/definePane";
import { usePanes } from "../../../main/panes/usePanes";
import { RiskAssessmentDocumentationOption, useRiskDocumentationPane } from "./RiskDocumentationPane.hooks";
import { RiskAssessmentDocumentViewModel } from "./RiskDocumentationPane.types";
import RiskAssessmentDocumentTable from "./RiskAssessmentDocumentTable";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

export interface RiskDocumentationPaneProps {
    riskAssessmentId: string;
}

// The preview manager works on the shared DocumentPreviewable shape, where the label lives in `name` and a link
// document carries its URL there. Risk documents store the label as the version's file name (or link).
const toPreviewable = (document: RiskAssessmentDocumentViewModel): DocumentPreviewable => ({
    documentId: document.documentId,
    name: document.fileName ?? document.link ?? "",
    fileName: document.fileName,
    link: document.link,
    dataId: document.dataId,
});

/**
 * A risk assessment's documentation, in the rail. A pane rather than the dialog it replaces so that opening
 * one of its documents leaves the list standing: the document takes the stage beside it, where a dialog
 * would have rendered in front of the stage and had to close itself to get out of the way.
 */
function RiskDocumentationPane(props: RiskDocumentationPaneProps) {
    const { translateString } = useTranslation();
    const { refetchPageBehindPanes } = usePanes();
    const {
        documents,
        documentsUploading,
        uploadPercentage,
        documentationOption,
        setDocumentationOption,
        link,
        setLink,
        createDocuments,
        uploadLink,
        removeDocument,
        updateDocument,
    } = useRiskDocumentationPane(props.riskAssessmentId, refetchPageBehindPanes);

    const { handleDocumentClick, getPreviewHref } = useDocumentPreviewManager({
        documents: (documents ?? []).map(toPreviewable),
        entityType: EntityType.Risk,
        entityId: props.riskAssessmentId,
    });

    return (
        <PaneLayout header={translateString("documentation")}>
            <RadioGroup
                row
                value={documentationOption}
                onChange={(e) => {
                    setDocumentationOption(Number((e.target as HTMLInputElement).value));
                    setLink(undefined);
                }}
            >
                <DotLegalRadioButton value={RiskAssessmentDocumentationOption.Upload} labelPlacement="end" label={translateString("uploadDocument")} />
                <DotLegalRadioButton value={RiskAssessmentDocumentationOption.Link} labelPlacement="end" label={translateString("insertLink")} />
            </RadioGroup>

            <Collapse in={documentationOption === RiskAssessmentDocumentationOption.Upload}>
                <DotLegalFileUploadWrapper
                    multiple
                    files={[]}
                    isLoading={documentsUploading}
                    onFilesChanged={(files) => createDocuments(files)}
                    pasteEnabled={documentationOption === RiskAssessmentDocumentationOption.Upload}
                />
                <UploadProgressBar isUploading={documentsUploading} uploadPercentage={uploadPercentage} />
            </Collapse>

            <Collapse in={documentationOption === RiskAssessmentDocumentationOption.Link}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <DotLegalTextField
                        label={translateString("insertLink")}
                        value={link ?? null}
                        debounce={false}
                        errorText={""}
                        onChange={(value) => setLink(value)}
                        noMargin
                    />
                    <Typography
                        onClick={uploadLink}
                        sx={(theme) => ({
                            alignSelf: "end",
                            fontSize: theme.typography.pxToRem(12),
                            color: theme.palette.primary.main,
                            "&:hover": { cursor: "pointer", textDecoration: "underline" },
                        })}
                    >
                        {translateString("insertLink")}
                    </Typography>
                </Box>
            </Collapse>

            <Collapse in={!!documents?.length}>
                <Box marginTop={2}>
                    <DotLegalHeader marginTop={0} marginBottom={0.5} headerStyle="small">
                        {translateString("documents")}
                    </DotLegalHeader>
                    <RiskAssessmentDocumentTable
                        documents={documents ?? []}
                        onRemoveDocument={removeDocument}
                        onEditDocument={updateDocument}
                        onDocumentClick={(row) => handleDocumentClick(toPreviewable(row))}
                        getPreviewHref={(row) => getPreviewHref(toPreviewable(row))}
                    />
                </Box>
            </Collapse>
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: RiskDocumentationPane });
